<template>
  <b-form>
    <h3 class="mx-3" v-if="targetingidx === undefined">
      {{ target && target.name ? target.name : 'New targeting group' }}
    </h3>
    <span class="mx-3">
      {{ deal ? deal.name : 'Loading deal name' }}
    </span>
    <b-row v-if="deal">
      <b-col xl="7">
        <b-card title="General Settings" class="my-4">
          <b-form-row>
            <b-col>
              <b-form-group label="Group Name" description="Internal reference name of this targeting group">
                <b-input v-model="target.name" required />
              </b-form-group>
            </b-col>
          </b-form-row>
        </b-card>
        <b-card title="Partner Targeting" class="my-4">
          <b-form-row class="my-4">
            <b-col md="12">
              <b-form-group
                label="Networks"
                :description="BRAND + ' network is a catch-all for directly integrated publishers'">
                <mulselect
                  variant="light-muted"
                  :options="exchangeOptions"
                  option-value-field="company_id"
                  option-label-field="name"
                  :values="target.exchanges">
                </mulselect>
              </b-form-group>
            </b-col>
          </b-form-row>

          <cool-label>Ad Formats</cool-label>
          <b-form-row>
            <b-col md="3">
              <b-form-group>
                <b-checkbox
                  v-model="target.formats.banner">
                  <small>Banner</small>
                </b-checkbox>
              </b-form-group>
            </b-col>
            <b-col md="3">
              <b-form-group>
                <b-checkbox
                  v-model="target.formats.video">
                  <small>Video</small>
                </b-checkbox>
              </b-form-group>
            </b-col>
            <b-col md="3">
              <b-form-group>
                <b-checkbox
                  v-model="target.formats.native">
                  <small>Native</small>
                </b-checkbox>
              </b-form-group>
            </b-col>
          </b-form-row>

          <b-form-row class="mt-4">
            <b-col md="12">
              <cool-label>
                Countries
                <b-badge disabled v-if="!target.countries.length" class="ml-2">All Countries</b-badge>
              </cool-label>
              <b-form-group
                description="Select countries to apply deal to">
                <mulselect
                  variant="light-muted"
                  :options="countryOptions"
                  option-value-field="value"
                  option-label-field="name"
                  :values="target.countries">
                </mulselect>
              </b-form-group>
            </b-col>
          </b-form-row>

          <b-form-row class="mt-4">
            <b-col md="12">
              <cool-label>
                Publisher IDs
                <b-badge disabled v-if="!target.pub_ids.length">All Publishers</b-badge>
              </cool-label>
              <b-form-group>
                <string-list-editor :values="target.pub_ids"
                                    placeholder="Enter publisher ID">
                </string-list-editor>
              </b-form-group>
            </b-col>
          </b-form-row>

          <b-form-row class="mt-4">
            <b-col md="12">
              <cool-label>
                Zone IDs
                <b-badge disabled v-if="!target.zone_ids.length">All Publishers</b-badge>
              </cool-label>
              <b-form-group>
                <string-list-editor :values="target.zone_ids"
                                    placeholder="Enter zone or tag ID">
                </string-list-editor>
              </b-form-group>
            </b-col>
          </b-form-row>

          <b-form-row class="mt-4">
            <b-col md="12">
              <cool-label>
                App Bundles
                <b-badge disabled v-if="!target.bundles.length">All Bundles</b-badge>
              </cool-label>
              <b-form-group>
                <string-list-editor :values="target.bundles"
                                    placeholder="Enter app bundle value">
                </string-list-editor>
              </b-form-group>
            </b-col>
          </b-form-row>

        </b-card>
        <b-card class="mt-4" title="Category Targeting">
          <b-form-row class="mb-3">
            <b-col>
              <b-form-group
                label="IAB Category Inclusion"
                description="Target only when IAB category is present">
                <mulselect
                  :options="cats"
                  :values="target.cats"
                  :limit="6">
                </mulselect>
              </b-form-group>
            </b-col>
          </b-form-row>

          <b-form-row>
            <b-col>
              <b-form-group
                label="BCAT Exclusion Targeting"
                description="Apply deal only when this bcat value is NOT present">
                <mulselect
                  :options="cats"
                  :values="target.bcats_excl"
                  :limit="5">
                </mulselect>
              </b-form-group>
            </b-col>
          </b-form-row>
        </b-card>

        <b-card class="mt-4" title="Device Targeting">
          <cool-label>
            Platforms
            <b-badge disabled v-if="!target.device.platforms.length">All Platforms</b-badge>
          </cool-label>
          <b-form-row class="mb-4">
            <b-col md="12">
              <b-form-group
                label=""
                description="Select platforms to apply deal to, none selected means all by default">
                <mulselect
                  variant="light-muted"
                  :options="platformOptions"
                  option-value-field="value"
                  option-label-field="text"
                  :values="target.device.platforms">
                </mulselect>
              </b-form-group>
            </b-col>
          </b-form-row>

          <cool-label>Target Device Types</cool-label>
          <b-form-row>
            <b-col md="2">
              <b-form-group>
                <b-checkbox
                  v-model="target.device.types.desktop">
                  <small>Desktop</small>
                </b-checkbox>
              </b-form-group>
            </b-col>
            <b-col md="2">
              <b-form-group>
                <b-checkbox
                  v-model="target.device.types.mobile">
                  <small>Smartphone</small>
                </b-checkbox>
              </b-form-group>
            </b-col>
            <b-col md="2">
              <b-form-group>
                <b-checkbox
                  v-model="target.device.types.tablet">
                  <small>Tablet</small>
                </b-checkbox>
              </b-form-group>
            </b-col>
            <b-col md="2">
              <b-form-group>
                <b-checkbox
                  v-model="target.device.types.settop">
                  <small>Set-top</small>
                </b-checkbox>
              </b-form-group>
            </b-col>
            <b-col md="2">
              <b-form-group>
                <b-checkbox
                  v-model="target.device.types.ctv">
                  <small>Connected TV</small>
                </b-checkbox>
              </b-form-group>
            </b-col>
          </b-form-row>
        </b-card>
      </b-col>

      <b-col>
        <b-card class="mt-4" title="Sovrn Audiences">
          <b-form-row>
            <b-col>
              <b-form-group
                label="Sovrn Audiences (Purchase, Interest, Lookalike)"
                description="Apply deal only if matching one of these generated sovrn audiences">
                <mulselect
                  :options="normalizedSovrnCats"
                  :values="target.commerce_cats"
                  :limit="5">
                </mulselect>
              </b-form-group>
            </b-col>
          </b-form-row>
        </b-card>

        <b-card class="mt-4" title="Video Targeting" v-if="target.formats.video">
          <h5 class="mt-4">Livestream</h5>
          <b-form-row>
            <b-col md="3">
              <b-form-group>
                <b-radio name="video-live"
                         value="ANY"
                         v-model="target.video.livestream">
                  <small>Any</small>
                </b-radio>
              </b-form-group>
            </b-col>
            <b-col md="3">
              <b-form-group>
                <b-checkbox name="video-live"
                            value="NOTLIVE"
                            v-model="target.video.livestream">
                  <small>Not Live</small>
                </b-checkbox>
              </b-form-group>
            </b-col>
            <b-col md="3">
              <b-form-group>
                <b-checkbox name="video-live"
                            value="LIVE"
                            v-model="target.video.livestream">
                  <small>Live Only</small>
                </b-checkbox>
              </b-form-group>
            </b-col>
          </b-form-row>

          <h5 class="mt-5">
            Language <b-badge disabled v-if="!target.video.languages.length">All Languages</b-badge>
            <b-btn v-if="target.video.languages.length" class="btn-sm mt-auto" @click="target.video.languages = []">Clear</b-btn>
          </h5>
          <b-form-row>
            <b-col md="12">
              <b-form-group
                label="Language(s)"
                description="Select languages to apply deal to">
                <mulselect
                  variant="light-muted"
                  :options="langOptions"
                  option-value-field="value"
                  option-label-field="text"
                  :values="target.video.languages">
                </mulselect>
              </b-form-group>
            </b-col>
          </b-form-row>

          <h5 class="mt-5">
            Ratings <b-badge disabled v-if="!target.video.ratings.length">All Languages</b-badge>
            <b-btn v-if="target.video.ratings.length" class="btn-sm mt-auto" @click="target.video.ratings = []">Clear</b-btn>
          </h5>
          <b-form-row>
            <b-col md="12">
              <b-form-group
                label="TV Rating(s)"
                description="Select tv ratings to apply deal to">
                <mulselect
                  variant="light-muted"
                  :options="videoRatingOptions"
                  option-value-field="value"
                  option-label-field="text"
                  :values="target.video.ratings">
                </mulselect>
              </b-form-group>
            </b-col>
          </b-form-row>

          <h5 class="mt-5">
            Content Genres <b-badge disabled v-if="!target.video.genres.length">All genres</b-badge>
            <b-btn v-if="target.video.genres.length" class="btn-sm mt-auto" @click="target.video.genres = []">Clear</b-btn>
          </h5>
          <b-form-row>
            <b-col md="12">
              <b-form-group
                label="Video genres(s)"
                description="Select genres to apply deal to">
                <mulselect
                  variant="light-muted"
                  :options="videoGenreOptions"
                  option-value-field="value"
                  option-label-field="text"
                  :values="target.video.genres">
                </mulselect>
              </b-form-group>
            </b-col>
          </b-form-row>
        </b-card>
      </b-col>
    </b-row>
    <b-btn @click="save()" class="mt-4" variant="primary">Save</b-btn>
  </b-form>
</template>

<script>
import Mulselect from '@/components/Mulselect'
import StringListEditor from "@/components/StringListEditor"
import {DemandApi} from "../api";
import { ExchangeApi } from '@/exchange/api'
import {MiscApi} from "../../api"
import ManySelector from "../../components/ManySelector.vue";
import iab from '@/assets/iab';

export default {
  name: 'DealTargeting',
  props: {
    dealid: { required: true },
    targetingidx: { required: false }
  },
  mounted () {
    this.getExchangeList()
    this.getCountryList()
    this.getCommerceCatsList()

    this.getDeal(this.dealid).then(deal => {
      this.deal = deal

      if (this.targetingidx >= 0) {
        // merge new defaults if missing
        const d = deal.targeting[this.targetingidx]

        for (const key of Object.keys(this.target)) {
          if (d[key] === undefined) {
            d[key] = this.target[key]
          }
        }

        this.target = d
      }
    })
  },
  data () {
    return {
      BRAND,
      deal: null,
      target: {
        name: null,
        exchanges: [],
        countries: [],
        device: {
          platforms: [],
          types: {}
        },
        video: {
          livestream: 'ANY',
          languages: [],
          ratings: [],
          genres: []
        },
        formats: {
          banner: false,
          video: false,
          native: false
        },
        pub_ids: [],
        zone_ids: [],
        bundles: [],
        cats: [],
        bcats_excl: [],
        commerce_cats: []
      },
      exchangeOptions: [],
      countryOptions: [],
      platformOptions: [
        { value: "android", text: "Android"},
        { value: "ios", text: "iOS"},
        { value: "windows", text: "Windows"},
        { value: "osx", text: "Mac OS X"},
        { value: "roku", text: "Roku"},
        { value: "apple_tv", text: "Apple TV"},
        { value: "web_os", text: "Web OS"},
        { value: "tizen", text: "Tizen"},
        { value: "os_other", text: "All Other"},
      ],
      langOptions: [
        { value: "en", text: "English" },
        { value: "es", text: "Spanish" },
        { value: "fr", text: "French" },
        { value: "de", text: "German" },
        { value: "pt", text: "Portuguese" },
        { value: "it", text: "Italian" },
      ],
      videoRatingOptions: [
        { value: "G", text: "(Movie) G General Audiences" },
        { value: "PG", text: "(Movie) PG Parental Guidance" },
        { value: "PG13", text: "(Movie) PG-13 Parents Cautioned" },
        { value: "R", text: "(Movie) R restricted" },
        { value: "NC17", text: "(Movie) NC-17 Adult" },
        { value: "TVG", text: "(TV) TV-G General" },
        { value: "TVPG", text: "(TV) TV-PG Parental Guidance" },
        { value: "TV14", text: "(TV) TV-14 Parents Cautioned" },
        { value: "TVMA", text: "(TV) TV-MA Mature" },
      ],
      videoGenreOptions: [
        "Movies",
        "Music",
        "Television",
        "Sports",
        "Fitness",
        "Travel",
        "Food",
        "Game Shows",
        "Gaming",
        "News",
        "Business",
        "Tech",
        "Nature",
        "Action/Adventure",
        "Animation & Anime",
        "Awards Shows",
        "Biographies",
        "Comedy",
        "Documentary",
        "Drama",
        "Factual",
        "Family/Children",
        "Fantasy",
        "History",
        "Holidays",
        "Horror",
        "Lifestyle",
        "Music Video",
        "Musicals",
        "Mystery",
        "Reality TV",
        "Romance",
        "Science Fiction",
        "Soap Opera",
        "Special Interest (Indie/Art House)",
        "Talk Radio",
        "Public Radio",
        "Talk Show",
        "True Crime",
        "Western",
        "Young Adult"
      ],
      commerceCatOptions: []
    }
  },
  methods: {
    save () {
      let d = this.deal
      const t = this.target

      let oneDev = false
      for (let bool of Object.values(t.device.types)) {
        oneDev = oneDev || bool;
      }

      if (!oneDev) {
        return this.$notify.error("Please enable at least one device type")
      }

      if (!t.name) {
        return this.$notify.error("Please fill in targeting group name")
      }

      if (!t.formats.banner && !t.formats.video && !t.formats.native) {
        return this.$notify.error('Please select at least one ad format')
      }

      /*
      no longer the case at the moment
      if (d.type && d.type.indexOf('PRIVATE') > -1 && (!t.zone_ids.length || !t.exchanges.length)) {
        // special carveout of safety for BSW as they have work on our end done to
        // concurrently auction private deals and dup the imp into open auction at the same time
        if (!d.wdsps || !d.wdsps['26'])
          return this.$notify.error('Private deals MUST have network and tag ID(s) targeting')
      }
       */

      if (this.targetingidx === null || this.targetingidx === undefined) {
        d.targeting.push(t)
      }

      DemandApi.dealSave(d).then((deal) => {
        this.$notify.success("Targeting group saved")

        this.$router.push({ name: 'AdminDealSettings', params: { dealid: deal.id }})
      })
    },
    getDeal(dealId) {
      return DemandApi.dealGet(dealId).then(deal => {
        return deal
      }).catch(e => {
        this.$notify.error('Failed loading deal: ' + e)
      })
    },
    getExchangeList() {
      ExchangeApi.list().then((data) => {
        this.exchangeOptions.push(...data)
      })
    },
    getCountryList() {
      MiscApi.getCountryList().then((data) => {
        let set = new Set(data.countries)
        this.countryOptions.push(...set.keys())
      }, (err) => {
        console.warn(err)
      })
    },
    getCommerceCatsList() {
      DemandApi.commerceCatsList().then(list => {
        if (list == null || !list.length) {
          return this.$notify.error('Failed loading commerce audience list')
        }

        this.commerceCatOptions.push(...list)
      })
    }
  },
  computed: {
    cats () {
      const list = []
      Object.keys(iab).forEach(value => {
        list.push({ value, label: (value + ' ' + iab[value]) })
      })
      return list
    },
    normalizedSovrnCats () {
      const list = []
      if (this.commerceCatOptions === null)
        return list;

      this.commerceCatOptions.forEach((audienceSegment) => {
        const popString = audienceSegment.population > 0 ?
          audienceSegment.population > 1000000 ? (Math.round(audienceSegment.population / 1000000) + 'm')
          : (Math.max(Math.round(audienceSegment.population / 1000), 1) + 'k') : 'empty!'

        list.push({
          value: audienceSegment.segmentId,
          label: '(' + audienceSegment.segmentId +')\t' + audienceSegment.category + '\t' + popString
        })
      });

      return list;
    }
  },
  components: {
    Mulselect,
    StringListEditor,
    ManySelector
  }
}
</script>



// WEBPACK FOOTER //
// src/demand/pages/DealTargeting.vue